import { useEffect, useState } from "react";

type NewsImage = {
  url: string;
  width: number;
  height: number;
};

export interface ProductNewsFeedItem {
  title: string;
  url: string;
  description: string;
  createdAt: string;
  image?: NewsImage;
  author?: {
    name: string;
  };
}

interface CachedNewsFeed {
  data: ProductNewsFeedItem[];
  timestamp: number;
}

const CACHE_KEY = "product_news_feed_cache";
const CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds

const useProductNewsFeed = () => {
  const [newsFeed, setNewsFeed] = useState<ProductNewsFeedItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cache management helpers
  const saveToCache = (data: ProductNewsFeedItem[]) => {
    const cacheData: CachedNewsFeed = {
      data,
      timestamp: Date.now(),
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
  };

  const getCachedData = (): ProductNewsFeedItem[] | null => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) return null;

      const cacheData: CachedNewsFeed = JSON.parse(cached);
      const isExpired = Date.now() - cacheData.timestamp >= CACHE_DURATION;

      if (isExpired) {
        localStorage.removeItem(CACHE_KEY);
        return null;
      }

      return cacheData.data;
    } catch {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
  };

  const clearCache = () => {
    localStorage.removeItem(CACHE_KEY);
  };

  // API helpers
  const fetchNewsFeed = async (): Promise<ProductNewsFeedItem[]> => {
    const BASE_URL = import.meta.env.VITE_SITE_URL;
    const FEED_URL = `${BASE_URL}/api/blog-posts`;

    if (!BASE_URL) {
      return [];
    }
    const response = await fetch(FEED_URL);

    if (!response.ok) {
      console.error(`Failed to fetch news feed: ${response.status}`);
      return [];
    }

    const data = await response.json();

    const posts = data.posts;

    return posts as ProductNewsFeedItem[];
  };

  const loadNewsFeed = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await fetchNewsFeed();
      saveToCache(data);
      setNewsFeed(data);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch news feed";
      setError(errorMessage);
      console.error("Error fetching news feed:", err);
    } finally {
      setLoading(false);
    }
  };

  // Main effect
  useEffect(() => {
    const cachedData = getCachedData();

    if (cachedData) {
      setNewsFeed(cachedData);
      return;
    }

    loadNewsFeed();
  }, []);

  return {
    newsFeed,
    loading,
    error,
    refresh: loadNewsFeed,
    clearCache,
  };
};

export default useProductNewsFeed;
